import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SPORT_GROUPS, SPORT_LABELS } from "@/lib/athleteTaxonomy";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSON_TYPES = [
    { value: "", label: "—" },
    { value: "parent", label: "Parent" },
    { value: "child", label: "Child" },
    { value: "adult_client", label: "Adult Client" },
];

const FLAT_SPORTS = [
    { value: "", label: "—" },
    ...SPORT_GROUPS.flatMap((g) => g.sports.map((s) => ({ value: s.value, label: s.label || SPORT_LABELS[s.value] || s.value }))),
];

/**
 * Profile metadata + roster assignment editor on the LeadDetail panel.
 * - Coach + owner can edit person_type / primary_sport.
 * - Owner ONLY can change assigned_coach.
 * - "Message" button quick-link to /coach/messages?to=<user_id>
 */
export default function LeadProfileMetaEditor({ lead, isOwner, coaches, onChanged }) {
    const navigate = useNavigate();
    const { user: me } = useAuth();
    // Per-coach permission: view_messages gates the Message button.
    const canMessage = me?.role === "owner" || !!me?.permissions?.view_messages;
    const [personType, setPersonType] = useState(lead.person_type || "");
    const [primarySport, setPrimarySport] = useState(lead.primary_sport || "");
    const [assignedCoachId, setAssignedCoachId] = useState(lead.assigned_coach_id || "");
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        setPersonType(lead.person_type || "");
        setPrimarySport(lead.primary_sport || "");
        setAssignedCoachId(lead.assigned_coach_id || "");
    }, [lead.user_id]);

    const saveMeta = async (next) => {
        setSaving("meta");
        try {
            await axios.patch(
                `${API}/coach/clients/${lead.user_id}/profile-meta`,
                next,
                // X-Retry-Safe: PATCH is idempotent — same body = same result.
                // Lets the global axios interceptor retry transient backend
                // restarts (Cloudflare 520) so the user doesn't see a
                // "Couldn't save" toast for a 2-second prod hiccup.
                { withCredentials: true, headers: { "X-Retry-Safe": "1" } },
            );
            onChanged?.();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(null);
        }
    };

    const saveAssignment = async (coachId) => {
        setSaving("coach");
        try {
            await axios.patch(
                `${API}/coach/clients/${lead.user_id}/assigned-coach`,
                { coach_user_id: coachId || null },
                { withCredentials: true, headers: { "X-Retry-Safe": "1" } },
            );
            toast.success(coachId ? "Assigned" : "Unassigned");
            onChanged?.();
        } catch (e) {
            toast.error("Couldn't update", { description: e?.response?.data?.detail });
        } finally {
            setSaving(null);
        }
    };

    const startMessage = async () => {
        try {
            const r = await axios.post(
                `${API}/messages/start-thread`,
                { recipient_user_id: lead.user_id, body: "_" },
                { withCredentials: true },
            ).catch((e) => {
                if (e?.response?.data?.detail) throw e;
            });
            // Backend's start-thread requires body but we just want the thread_id;
            // it's idempotent so we use a noop body. Backend may reject — fall back.
            navigate(`/coach/messages?t=${r.data.thread_id}`);
        } catch (e) {
            // Fallback: just go to the inbox; the row will be there once a message is sent.
            navigate(`/coach/messages`);
            toast.info("Open the inbox and send your first message");
        }
    };

    return (
        <div data-testid="lead-profile-meta" className="p-6 border-b border-zinc-900 space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Profile</div>
                {canMessage && (
                    <button
                        type="button"
                        onClick={startMessage}
                        data-testid="open-message-thread"
                        className="text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 bg-brand-red text-white font-semibold inline-flex items-center gap-1.5"
                    >
                        <MessageSquare className="w-3 h-3" /> Message
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 gap-2">
                <label className="block">
                    <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-400 block mb-1">Person type</span>
                    <select
                        value={personType}
                        data-testid="meta-person-type"
                        disabled={saving === "meta"}
                        onChange={(e) => { const v = e.target.value; setPersonType(v); saveMeta({ person_type: v }); }}
                        className="w-full bg-black border border-zinc-800 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-brand-red"
                    >
                        {PERSON_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-400 block mb-1">Sport</span>
                    <select
                        value={primarySport}
                        data-testid="meta-primary-sport"
                        disabled={saving === "meta"}
                        onChange={(e) => { const v = e.target.value; setPrimarySport(v); saveMeta({ primary_sport: v }); }}
                        className="w-full bg-black border border-zinc-800 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-brand-red"
                    >
                        {FLAT_SPORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </label>
            </div>
            {isOwner && (
                <label className="block">
                    <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-400 block mb-1">Assigned coach</span>
                    <select
                        value={assignedCoachId}
                        data-testid="meta-assigned-coach"
                        disabled={saving === "coach"}
                        onChange={(e) => { setAssignedCoachId(e.target.value); saveAssignment(e.target.value); }}
                        className="w-full bg-black border border-zinc-800 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-brand-red"
                    >
                        <option value="">— Unassigned —</option>
                        {(coaches || []).map((c) => (
                            <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>
                        ))}
                    </select>
                    {saving === "coach" && <Loader2 className="w-3 h-3 inline animate-spin text-brand-red ml-1" />}
                </label>
            )}
        </div>
    );
}
